import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useState } from "react";
import { buildAuthHeaders } from "../forum/authHeaders";

const normalize = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function ChatGifPicker({ open, onClose, onSelect }) {
  useSourceLanguage();
  const [gifs, setGifs] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [failed, setFailed] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetch("/api/chat/gifs/suggestions/", {
      headers: buildAuthHeaders(), credentials: "include", signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error("La galerie de GIF est indisponible.");
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error("La galerie de GIF est indisponible.");
      if (!controller.signal.aborted) { setGifs(data); setFailed([]); }
    }).catch(error => {
      if (!controller.signal.aborted) setError(error.message || "Impossible de charger les GIF.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [retry]);

  const categories = [...new Set(gifs.map(gif => gif.category).filter(Boolean))];
  const results = useMemo(() => {
    const words = normalize(query).trim().split(/\s+/).filter(Boolean);
    return gifs.filter(gif => (!category || gif.category === category) && words.every(word => normalize(`${gif.label} ${gif.category || ""} ${gif.tags || ""}`).includes(word)));
  }, [gifs, query, category]);

  if (!open) return null;
  return (
    <LocalizedElement elementType={"section"} copyAttributes={["aria-label"]} className="chat-gif-picker" aria-label={sourceAttribute("Galerie de GIF")} onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); onClose(); } }}>
      <div className="chat-gif-picker__heading"><strong><LocalizedText source={"Choisir un GIF"} /></strong><LocalizedElement elementType={"button"} copyAttributes={["aria-label"]} type="button" onClick={onClose} aria-label={sourceAttribute("Fermer la galerie de GIF")}><LocalizedText source={"Fermer"} /></LocalizedElement></div>
      <LocalizedElement elementType={"input"} copyAttributes={["aria-label","placeholder"]} autoFocus type="search" aria-label={sourceAttribute("Rechercher un GIF")} placeholder={sourceAttribute("Rechercher : merci, amour, chat…")} value={query} onChange={event => { setQuery(event.target.value); setCategory(""); }} />
      <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="chat-gif-picker__categories" aria-label={sourceAttribute("Catégories de GIF")}>
        {["", ...categories].map(item => <button key={item} type="button" aria-pressed={category === item} onClick={() => { setCategory(item); setQuery(""); }}>{item || <LocalizedText source={"Tous"} />}</button>)}
      </LocalizedElement>
      <p className="chat-gif-picker__status" role="status">{loading ? <LocalizedText source={"Chargement des GIF…"} /> : error || `${results.length} GIF disponibles`}</p>
      {error ? <button type="button" onClick={() => setRetry(value => value + 1)}><LocalizedText source={"Réessayer"} /></button> : null}
      {!loading && !error && !results.length ? <p><LocalizedText source={"Aucun GIF trouvé. Essaie un autre mot."} /></p> : null}
      <div className="chat-gif-picker__grid">
        {results.map(gif => <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]} type="button" className="chat-gif-picker__tile" key={gif.url} disabled={failed.includes(gif.url)} aria-label={sourceAttribute(`Choisir ${gif.label}`, "Choisir __ET_VALUE_0__")} onClick={() => onSelect(gif)}>
          {failed.includes(gif.url) ? <span><LocalizedText source={"Aperçu indisponible"} /></span> : <img src={gif.preview_url || gif.url} alt={gif.label} loading="lazy" onError={() => setFailed(current => [...current, gif.url])} />}
        </LocalizedElement>)}
      </div>
      <a href="https://giphy.com/" target="_blank" rel="noreferrer" className="chat-gif-picker__credit"><LocalizedText source={"GIFs via GIPHY"} /></a>
    </LocalizedElement>
  );
}
